import { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import {
  supabase,
  signIn,
  signOut,
  signUp,
  signInWithMagicLink,
  signUpWithInvitation,
  resetPassword,
  updatePassword,
  type UserProfile,
} from '../lib/auth';
import { apiUrl } from '../lib/api';

interface ViewAsPartner {
  partnerId: number;
  partnerName: string;
  returnTo?: string; // URL to return to when exiting partner view
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signUpWithInvitation: (
    token: string,
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  isAdmin: boolean;
  isOwner: boolean;
  isClientManager: boolean;
  isClientUser: boolean;
  isPartner: boolean;
  partnerId: number | null;
  clientId: number | null; // For client_user role
  assignedClientIds: number[] | null;
  canAccessClient: (clientId: number) => boolean;
  canSeeSensitiveData: boolean;
  // View as partner (admin only)
  viewAsPartner: ViewAsPartner | null;
  setViewAsPartner: (partner: ViewAsPartner | null) => void;
  isViewingAsPartner: boolean;
  actualIsAdmin: boolean; // true admin status (not affected by viewAsPartner)
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VIEW_AS_PARTNER_KEY = 'relo_view_as_partner';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsPartner, setViewAsPartnerState] = useState<ViewAsPartner | null>(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem(VIEW_AS_PARTNER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setViewAsPartner = useCallback((partner: ViewAsPartner | null) => {
    setViewAsPartnerState(partner);
    if (partner) {
      localStorage.setItem(VIEW_AS_PARTNER_KEY, JSON.stringify(partner));
    } else {
      localStorage.removeItem(VIEW_AS_PARTNER_KEY);
    }
  }, []);

  // Load profile from API (bypasses RLS issues)
  const loadProfile = async (accessToken: string): Promise<UserProfile | null> => {
    try {
      const response = await fetch(apiUrl('/api/auth/me'), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json() as {
        id: string;
        role: string;
        partner_id: number | null;
        client_id: number | null;
        full_name: string | null;
        assigned_client_ids: number[] | null;
        is_owner: boolean;
      };

      return {
        id: data.id,
        role: data.role,
        partner_id: data.partner_id,
        client_id: data.client_id ?? null,
        full_name: data.full_name || null,
        avatar_url: null,
        is_active: true,
        assigned_client_ids: data.assigned_client_ids ?? null,
        is_owner: data.is_owner === true,
      } as UserProfile;
    } catch {
      return null;
    }
  };

  // Handle session changes
  const handleSession = async (session: Session | null) => {
    if (session?.user && session.access_token) {
      setUser(session.user);
      const userProfile = await loadProfile(session.access_token);
      setProfile(userProfile);
    } else {
      setUser(null);
      setProfile(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    // Initialize auth - get current session.
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          await handleSession(session);
        }
      } catch {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (mounted && loading) {
        setLoading(false);
      }
    }, 5000);

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          await handleSession(session);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setLoading(false);
          // Clear view-as state so the next login doesn't inherit it
          setViewAsPartnerState(null);
          try {
            localStorage.removeItem(VIEW_AS_PARTNER_KEY);
          } catch {
            /* ignore */
          }
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    const { error } = await signIn(email, password);
    return { error: error as Error | null };
  }, []);

  const handleSignUp = useCallback(async (email: string, password: string, fullName?: string) => {
    const { error } = await signUp(email, password, fullName);
    return { error: error as Error | null };
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(null);
    setProfile(null);
    // Clear viewAsPartner state on logout
    setViewAsPartnerState(null);
    localStorage.removeItem(VIEW_AS_PARTNER_KEY);
  }, []);

  const handleMagicLink = useCallback(async (email: string) => {
    const { error } = await signInWithMagicLink(email);
    return { error: error as Error | null };
  }, []);

  const handleInvitationSignUp = useCallback(async (
    token: string,
    email: string,
    password: string,
    fullName: string
  ) => {
    const { error } = await signUpWithInvitation(token, email, password, fullName);
    return { error: error ? new Error((error as { message?: string }).message || 'Error') : null };
  }, []);

  const handleResetPassword = useCallback(async (email: string) => {
    const { error } = await resetPassword(email);
    return { error: error as Error | null };
  }, []);

  const handleUpdatePassword = useCallback(async (password: string) => {
    const { error } = await updatePassword(password);
    return { error: error as Error | null };
  }, []);

  const actualIsAdmin = profile?.role === 'admin';
  const isOwner = actualIsAdmin && profile?.is_owner === true;
  const isClientManager = profile?.role === 'client_manager';
  const isClientUser = profile?.role === 'client_user';
  const isViewingAsPartner = (actualIsAdmin || isClientManager) && viewAsPartner !== null;
  const assignedClientIds = profile?.assigned_client_ids ?? null;
  const clientId = profile?.client_id ?? null;

  const canAccessClient = useCallback((cId: number): boolean => {
    if (actualIsAdmin) return true;
    if (isClientManager && assignedClientIds?.includes(cId)) return true;
    if (isClientUser && clientId === cId) return true;
    return false;
  }, [actualIsAdmin, isClientManager, isClientUser, clientId, assignedClientIds]);

  const canSeeSensitiveData = actualIsAdmin;

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo<AuthContextType>(() => ({
    user,
    profile,
    loading,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    signInWithMagicLink: handleMagicLink,
    signUpWithInvitation: handleInvitationSignUp,
    resetPassword: handleResetPassword,
    updatePassword: handleUpdatePassword,
    // When viewing as partner, override these values
    isAdmin: actualIsAdmin && !isViewingAsPartner,
    isOwner,
    isClientManager: isClientManager ?? false,
    isClientUser: isClientUser ?? false,
    isPartner: isViewingAsPartner || profile?.role === 'partner' || profile?.role === 'viewer',
    partnerId: isViewingAsPartner ? viewAsPartner?.partnerId ?? null : (profile?.partner_id ?? null),
    clientId,
    assignedClientIds,
    canAccessClient,
    canSeeSensitiveData,
    // View as partner features
    viewAsPartner,
    setViewAsPartner,
    isViewingAsPartner,
    actualIsAdmin,
  }), [
    user,
    profile,
    loading,
    handleSignIn,
    handleSignUp,
    handleSignOut,
    handleMagicLink,
    handleInvitationSignUp,
    handleResetPassword,
    handleUpdatePassword,
    actualIsAdmin,
    isOwner,
    isClientManager,
    isClientUser,
    clientId,
    isViewingAsPartner,
    viewAsPartner,
    setViewAsPartner,
    assignedClientIds,
    canAccessClient,
    canSeeSensitiveData,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
